import { useState, useRef, useEffect } from 'react';
import { useBottomSheet } from '@/hooks/useBottomSheet';
import { calcularPontosFinanceiro } from '@/utils/pontosFinanceiro';
import { useLimiteMensal } from '@/hooks/useLimiteMensal';
import { creditosFinanceiros, CreditoFinanceiro } from '@/mocks/creditosFinanceiros';
import { validarCodigoBarras, ResultadoValidacao } from '@/utils/validarCodigoBarras';
import BarcodeScanner from './BarcodeScanner';
import { getAuthSession } from '@/hooks/useAuth';
import { enviarEmailConfirmacaoTransacao } from '@/services/emailService';

interface PagamentoPersonalizadoModalProps {
  isOpen: boolean;
  onClose: () => void;
  pontosDisponiveis: number;
  userId?: string;
  creditoPreSelecionado?: CreditoFinanceiro;
}

type Etapa = 'formulario' | 'confirmacao' | 'token' | 'sucesso';

function gerarTransacaoId(): string {
  return (
    'PAG' +
    Date.now().toString(36).toUpperCase() +
    Math.random().toString(36).substring(2, 6).toUpperCase()
  );
}

export function PagamentoPersonalizadoModal({
  isOpen,
  onClose,
  pontosDisponiveis,
  userId = 'user-001',
  creditoPreSelecionado,
}: PagamentoPersonalizadoModalProps) {
  const limite = useLimiteMensal(userId, 'pagamento');

  const [creditoSelecionado, setCreditoSelecionado] = useState<string>(creditoPreSelecionado?.id ?? '');
  const [valorInput, setValorInput] = useState('');
  const [valorBloqueado, setValorBloqueado] = useState(false);
  const [codigoBarras, setCodigoBarras] = useState('');
  const [tipoConta, setTipoConta] = useState<'boleto' | 'consumo'>('boleto');
  const [resultadoValidacao, setResultadoValidacao] = useState<ResultadoValidacao | null>(null);
  const [validando, setValidando] = useState(false);
  const [etapa, setEtapa] = useState<Etapa>('formulario');
  const [erroValor, setErroValor] = useState('');
  const [processando, setProcessando] = useState(false);
  const [scannerAberto, setScannerAberto] = useState(false);

  const creditoAtual: CreditoFinanceiro | null = creditoPreSelecionado
    ? creditoPreSelecionado
    : (creditosFinanceiros.find((c) => c.id === creditoSelecionado) ?? null);

  // Token
  const [tokenDigits, setTokenDigits] = useState<string[]>(['', '', '', '', '', '']);
  const [tokenErro, setTokenErro] = useState('');
  const [reenvioTimer, setReenvioTimer] = useState(0);
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);
  const transacaoId = useRef('');
  const dataHora = useRef('');

  const valorNumerico = parseFloat(valorInput.replace(',', '.')) || 0;
  const pontosNecessarios = calcularPontosFinanceiro(valorNumerico);
  const validacao = limite.validarValor(valorNumerico);
  const saldoSuficiente = pontosDisponiveis >= pontosNecessarios;

  // Aviso de valor excedendo saldo do crédito
  const valorExcedeSaldo = creditoAtual !== null && valorNumerico > 0 && valorNumerico > creditoAtual.valor;
  const podeContinuar = !valorExcedeSaldo && valorNumerico > 0 && saldoSuficiente && validacao.valido && resultadoValidacao?.valido;

  useEffect(() => {
    let interval: ReturnType<typeof setInterval>;
    if (reenvioTimer > 0) {
      interval = setInterval(() => setReenvioTimer((p) => p - 1), 1000);
    }
    return () => clearInterval(interval);
  }, [reenvioTimer]);

  useEffect(() => {
    if (etapa === 'token') {
      setTimeout(() => inputRefs.current[0]?.focus(), 100);
    }
  }, [etapa]);

  const handleValorChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const raw = e.target.value.replace(/[^\d,]/g, '');
    setValorInput(raw);
    setErroValor('');
  };

  const processarCodigo = (codigo: string) => {
    const resultado = validarCodigoBarras(codigo);
    setResultadoValidacao(resultado);

    // Detecta tipo automaticamente
    if (resultado.tipo === 'consumo') {
      setTipoConta('consumo');
    } else if (resultado.tipo === 'boleto') {
      setTipoConta('boleto');
    }

    // Preenche valor automaticamente se identificado
    if (resultado.valor > 0) {
      setValorInput(resultado.valor.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }));
      setValorBloqueado(true);
    } else {
      setValorInput('');
      setValorBloqueado(false);
    }
  };

  const handleValidarCodigo = async () => {
    if (codigoBarras.length < 10) return;
    setValidando(true);
    await new Promise((r) => setTimeout(r, 600));
    processarCodigo(codigoBarras);
    setValidando(false);
  };

  const handleScannerDetectado = (codigo: string) => {
    setCodigoBarras(codigo);
    processarCodigo(codigo);
  };

  const handleContinuar = () => {
    if (!creditoSelecionado && !creditoPreSelecionado) { setErroValor('Selecione um crédito para continuar.'); return; }
    if (valorNumerico <= 0) { setErroValor('Informe um valor válido.'); return; }
    if (valorExcedeSaldo) {
      setErroValor(`O valor excede o saldo do crédito selecionado (R$ ${creditoAtual!.valor.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}).`);
      return;
    }
    const v = limite.validarValor(valorNumerico);
    if (!v.valido) { setErroValor(v.mensagem); return; }
    if (!saldoSuficiente) { setErroValor('Pontos insuficientes para este resgate.'); return; }
    if (!resultadoValidacao?.valido) { setErroValor('Valide o código de barras antes de continuar.'); return; }
    setEtapa('confirmacao');
  };

  const handleConfirmar = () => {
    setTokenDigits(['', '', '', '', '', '']);
    setTokenErro('');
    setReenvioTimer(60);
    setEtapa('token');
  };

  const handleTokenChange = (index: number, value: string) => {
    if (value && !/^\d$/.test(value)) return;
    const next = [...tokenDigits];
    next[index] = value;
    setTokenDigits(next);
    setTokenErro('');
    if (value && index < 5) inputRefs.current[index + 1]?.focus();
  };

  const handleTokenKeyDown = (index: number, e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Backspace' && !tokenDigits[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  const handleTokenPaste = (e: React.ClipboardEvent) => {
    e.preventDefault();
    const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
    if (pasted.length > 0) {
      const next = [...tokenDigits];
      for (let i = 0; i < pasted.length && i < 6; i++) next[i] = pasted[i];
      setTokenDigits(next);
      inputRefs.current[Math.min(pasted.length, 5)]?.focus();
    }
  };

  const tokenCompleto = tokenDigits.every((d) => d !== '');

  const handleValidarToken = async () => {
    if (!tokenCompleto) return;
    setProcessando(true);
    await new Promise((r) => setTimeout(r, 1800));
    if (tokenDigits.join('') === '000000') {
      setTokenErro('Token inválido. Verifique e tente novamente.');
      setProcessando(false);
      return;
    }
    transacaoId.current = gerarTransacaoId();
    dataHora.current = new Date().toLocaleString('pt-BR');
    limite.registrarResgate(valorNumerico);

    // Enviar email de confirmação via API do backend (fire-and-forget)
    const session = getAuthSession();
    if (session.email && session.nome) {
      const agora = new Date();
      enviarEmailConfirmacaoTransacao({
        nomeParticipante: session.nome,
        emailParticipante: session.email,
        tipo: 'pagamento',
        valor: valorNumerico,
        data: agora.toLocaleDateString('pt-BR'),
        hora: agora.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' }),
        idTransacao: transacaoId.current,
        descricao: resultadoValidacao?.beneficiario
          ? `Pagamento de conta - ${resultadoValidacao.beneficiario}`
          : 'Pagamento de conta',
      });
    }

    setProcessando(false);
    setEtapa('sucesso');
  };

  const handleFechar = () => {
    setCreditoSelecionado('');
    setValorInput('');
    setValorBloqueado(false);
    setCodigoBarras('');
    setTipoConta('boleto');
    setResultadoValidacao(null);
    setEtapa('formulario');
    setErroValor('');
    setTokenDigits(['', '', '', '', '', '']);
    setTokenErro('');
    setReenvioTimer(0);
    onClose();
  };

  const { mounted, sheetClass, backdropClass, handleClose: animatedClose } = useBottomSheet({ isOpen, onClose: handleFechar });

  if (!mounted) return null;

  const etapaIndex = { formulario: 0, confirmacao: 1, token: 2, sucesso: 3 };

  // Vencimento formatado
  const vencimentoFormatado = resultadoValidacao?.vencimento
    ? new Date(resultadoValidacao.vencimento + 'T12:00:00').toLocaleDateString('pt-BR')
    : 'Não informado';

  // Verifica se vencimento está vencido
  const vencido = resultadoValidacao?.vencimento
    ? new Date(resultadoValidacao.vencimento + 'T23:59:59') < new Date()
    : false;

  return (
    <>
      {/* Scanner de câmera */}
      <BarcodeScanner
        isOpen={scannerAberto}
        onClose={() => setScannerAberto(false)}
        onDetected={handleScannerDetectado}
      />

      <div
        className={`fixed inset-0 bg-black/50 flex items-end sm:items-center justify-center sm:p-4 z-50 ${backdropClass}`}
        onClick={animatedClose}
      >
        <div
          className={`bg-white sm:rounded-2xl rounded-t-2xl max-w-md w-full flex flex-col ${sheetClass}`}
          style={{ maxHeight: 'calc(100dvh - 64px)' }}
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="bg-gradient-to-r from-teal-500 to-cyan-600 text-white px-5 py-4 rounded-t-2xl flex-shrink-0">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center">
                  <i className="ri-bill-line text-xl"></i>
                </div>
                <div>
                  <h3 className="text-lg font-bold">Pagamento — Valor Personalizado</h3>
                  <p className="text-xs opacity-90">
                    {etapa === 'formulario' && 'Informe o valor da conta'}
                    {etapa === 'confirmacao' && 'Confirme os dados'}
                    {etapa === 'token' && 'Verificação de segurança'}
                    {etapa === 'sucesso' && 'Pagamento concluído!'}
                  </p>
                </div>
              </div>
              {etapa !== 'sucesso' && (
                <button
                  onClick={animatedClose}
                  className="w-8 h-8 bg-white/20 rounded-full flex items-center justify-center hover:bg-white/30 transition-colors cursor-pointer"
                >
                  <i className="ri-close-line text-lg"></i>
                </button>
              )}
            </div>
            <div className="flex gap-1.5">
              {(['formulario', 'confirmacao', 'token', 'sucesso'] as Etapa[]).map((e, i) => (
                <div
                  key={e}
                  className={`h-1 flex-1 rounded-full transition-all ${
                    i <= etapaIndex[etapa] ? 'bg-white' : 'bg-white/30'
                  }`}
                />
              ))}
            </div>
          </div>

          <div className="overflow-y-auto flex-1 px-5 py-4 pb-20">
            {/* ── ETAPA 1: FORMULÁRIO ── */}
            {etapa === 'formulario' && (
              <div className="space-y-4">
                {/* Saldo de pontos */}
                <div className="bg-teal-50 border border-teal-200 rounded-xl p-3 flex items-center justify-between">
                  <div>
                    <p className="text-xs text-teal-700">Seus pontos disponíveis</p>
                    <p className="text-xl font-bold text-teal-700">
                      {pontosDisponiveis.toLocaleString('pt-BR')} pts
                    </p>
                  </div>
                  <div className="w-10 h-10 flex items-center justify-center">
                    <i className="ri-coin-line text-2xl text-teal-500"></i>
                  </div>
                </div>

                {/* Limite mensal */}
                <div className="bg-slate-50 border border-slate-200 rounded-xl p-4 space-y-2">
                  <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Limite Mensal</p>
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-600">Limite mensal</span>
                    <span className="font-semibold text-slate-800">
                      {calcularPontosFinanceiro(limite.config.limiteTotal).toLocaleString('pt-BR')} pts
                    </span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-600">Já utilizado</span>
                    <span className="font-semibold text-orange-600">
                      {calcularPontosFinanceiro(limite.consumo.valorTotalResgatado).toLocaleString('pt-BR')} pts
                    </span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-600">Disponível</span>
                    <span className="font-bold text-teal-600">
                      {calcularPontosFinanceiro(limite.valorDisponivel).toLocaleString('pt-BR')} pts
                    </span>
                  </div>
                  <div className="h-2 bg-slate-200 rounded-full overflow-hidden mt-1">
                    <div
                      className="h-full bg-gradient-to-r from-orange-400 to-orange-500 rounded-full transition-all"
                      style={{ width: `${limite.percentualUtilizado}%` }}
                    />
                  </div>
                </div>

                {/* Seleção de crédito */}
                <div>
                  <label htmlFor="credito-select-pagamento-personalizado" className="block text-xs font-medium text-slate-700 mb-1.5">Crédito a utilizar</label>
                  {creditoPreSelecionado ? (
                    <div className="w-full px-3 py-2.5 border border-slate-200 rounded-xl bg-slate-50 text-sm text-slate-700 flex items-center gap-2">
                      <i className="ri-lock-line text-slate-400 flex-shrink-0"></i>
                      <span className="font-semibold truncate">{creditoPreSelecionado.descricao}</span>
                      <span className="text-slate-400 text-xs ml-auto whitespace-nowrap">R$ {creditoPreSelecionado.valor.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}</span>
                    </div>
                  ) : (
                    <select
                      id="credito-select-pagamento-personalizado"
                      data-tour="pagamento-credito-select"
                      value={creditoSelecionado}
                      onChange={(e) => { setCreditoSelecionado(e.target.value); setErroValor(''); }}
                      className="w-full px-3 py-2.5 border border-slate-200 rounded-xl text-sm bg-white focus:outline-none focus:ring-2 focus:ring-teal-400 cursor-pointer"
                    >
                      <option value="">Selecione um crédito</option>
                      {creditosFinanceiros.map((credito) => (
                        <option key={credito.id} value={credito.id}>
                          {credito.descricao} - R$ {credito.valor.toLocaleString('pt-BR', { minimumFractionDigits: 2 })} ({credito.campanha})
                        </option>
                      ))}
                    </select>
                  )}
                  {creditoAtual && (
                    <div className="mt-2 bg-teal-50 border border-teal-200 rounded-xl p-3 space-y-1">
                      <div className="flex justify-between text-xs">
                        <span className="text-slate-500">Campanha</span>
                        <span className="font-semibold text-slate-800">{creditoAtual.campanha}</span>
                      </div>
                      <div className="flex justify-between text-xs">
                        <span className="text-slate-500">Data do crédito</span>
                        <span className="font-semibold text-slate-800">{creditoAtual.dataCredito}</span>
                      </div>
                      <div className="flex justify-between text-xs">
                        <span className="text-slate-500">Saldo disponível</span>
                        <span className="font-bold text-teal-700">R$ {creditoAtual.valor.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}</span>
                      </div>
                    </div>
                  )}
                </div>

                {/* Tipo de conta */}
                <div>
                  <label className="block text-xs font-medium text-slate-700 mb-1.5">Tipo de Pagamento</label>
                  <div className="grid grid-cols-2 gap-2">
                    {([
                      { id: 'boleto', label: 'Boleto', icon: 'ri-file-text-line' },
                      { id: 'consumo', label: 'Conta de Consumo', icon: 'ri-lightbulb-line' },
                    ] as const).map((tipo) => (
                      <button
                        key={tipo.id}
                        type="button"
                        onClick={() => {
                          setTipoConta(tipo.id);
                          setResultadoValidacao(null);
                          setCodigoBarras('');
                          setValorInput('');
                          setValorBloqueado(false);
                        }}
                        className={`flex items-center gap-2 px-3 py-2.5 rounded-xl border-2 transition-all cursor-pointer text-sm ${
                          tipoConta === tipo.id
                            ? 'border-teal-500 bg-teal-50 text-teal-700'
                            : 'border-slate-200 text-slate-600 hover:border-slate-300'
                        }`}
                      >
                        <i className={tipo.icon}></i>
                        {tipo.label}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Código de barras */}
                <div>
                  <div className="flex items-center justify-between mb-1.5">
                    <label className="block text-xs font-medium text-slate-700">
                      Código de Barras / Linha Digitável
                    </label>
                    {/* Botão câmera — visível em mobile */}
                    <button
                      type="button"
                      onClick={() => setScannerAberto(true)}
                      className="flex items-center gap-1.5 px-2.5 py-1 bg-teal-500 text-white rounded-lg text-xs font-semibold hover:bg-teal-600 transition-colors cursor-pointer whitespace-nowrap"
                      title="Escanear com câmera"
                    >
                      <i className="ri-qr-scan-2-line"></i>
                      Escanear
                    </button>
                  </div>
                  <div className="flex gap-2">
                    <input
                      type="text"
                      value={codigoBarras}
                      onChange={(e) => {
                        const novoCodigo = e.target.value.replace(/\D/g, '');
                        setCodigoBarras(novoCodigo);
                        if (novoCodigo === '') {
                          setResultadoValidacao(null);
                          setValorInput('');
                          setValorBloqueado(false);
                        } else {
                          setResultadoValidacao(null);
                        }
                      }}
                      placeholder="44, 47 ou 48 dígitos"
                      className="flex-1 px-3 py-2.5 border border-slate-200 rounded-xl text-sm font-mono focus:outline-none focus:ring-2 focus:ring-teal-400"
                    />
                    <button
                      type="button"
                      onClick={handleValidarCodigo}
                      disabled={codigoBarras.length < 10 || validando}
                      className="px-3 py-2.5 bg-teal-500 text-white rounded-xl text-sm font-semibold hover:bg-teal-600 transition-colors cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap flex items-center gap-1"
                    >
                      {validando ? (
                        <i className="ri-loader-4-line animate-spin"></i>
                      ) : (
                        <i className="ri-search-line"></i>
                      )}
                      Validar
                    </button>
                  </div>

                  {/* Dica de comprimento */}
                  {codigoBarras.length > 0 && codigoBarras.length < 44 && (
                    <p className="text-xs text-slate-400 mt-1">
                      {codigoBarras.length} dígitos — mínimo 44 para boleto, 47 para linha digitável, 48 para conta de consumo
                    </p>
                  )}

                  {/* Resultado da validação */}
                  {resultadoValidacao && (
                    <div className={`mt-2 rounded-xl p-3 space-y-1.5 border ${
                      resultadoValidacao.valido
                        ? 'bg-teal-50 border-teal-200'
                        : 'bg-red-50 border-red-200'
                    }`}>
                      <div className={`flex items-center gap-2 text-xs font-semibold mb-1 ${
                        resultadoValidacao.valido ? 'text-teal-700' : 'text-red-700'
                      }`}>
                        <i className={resultadoValidacao.valido ? 'ri-checkbox-circle-line' : 'ri-error-warning-line'}></i>
                        {resultadoValidacao.valido ? 'Código válido' : 'Código inválido'}
                        {resultadoValidacao.valido && (
                          <span className="ml-1 px-1.5 py-0.5 bg-teal-100 text-teal-700 rounded-full text-[10px] font-bold uppercase">
                            {resultadoValidacao.tipo === 'boleto' ? 'Boleto' : 'Consumo'}
                          </span>
                        )}
                      </div>

                      {resultadoValidacao.valido ? (
                        <>
                          {resultadoValidacao.beneficiario && (
                            <div className="flex justify-between text-xs">
                              <span className="text-slate-500">Beneficiário / Segmento</span>
                              <span className="font-semibold text-slate-800">{resultadoValidacao.beneficiario}</span>
                            </div>
                          )}
                          {resultadoValidacao.banco && (
                            <div className="flex justify-between text-xs">
                              <span className="text-slate-500">Banco</span>
                              <span className="font-semibold text-slate-800">{resultadoValidacao.banco}</span>
                            </div>
                          )}
                          <div className="flex justify-between text-xs">
                            <span className="text-slate-500">Vencimento</span>
                            <span className={`font-semibold ${vencido ? 'text-red-600' : 'text-slate-800'}`}>
                              {vencimentoFormatado}
                              {vencido && <span className="ml-1 text-red-600">(Vencido)</span>}
                            </span>
                          </div>
                          <div className="flex justify-between text-xs pt-1 border-t border-teal-100 mt-1">
                            <span className="text-slate-500">Valor identificado</span>
                            <span className={`font-bold ${resultadoValidacao.valor > 0 ? 'text-teal-700' : 'text-amber-600'}`}>
                              {resultadoValidacao.valor > 0
                                ? `R$ ${resultadoValidacao.valor.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}`
                                : 'Não identificado — informe manualmente'}
                            </span>
                          </div>
                        </>
                      ) : (
                        <ul className="space-y-0.5">
                          {resultadoValidacao.erros.map((erro, i) => (
                            <li key={i} className="text-xs text-red-700 flex items-start gap-1">
                              <i className="ri-close-line flex-shrink-0 mt-0.5"></i>
                              {erro}
                            </li>
                          ))}
                        </ul>
                      )}
                    </div>
                  )}

                  {/* Aviso de boleto vencido */}
                  {vencido && resultadoValidacao?.valido && (
                    <div className="mt-2 flex items-start gap-2 bg-red-50 border border-red-200 rounded-xl p-3">
                      <i className="ri-alarm-warning-line text-red-500 flex-shrink-0 mt-0.5"></i>
                      <p className="text-xs text-red-700 font-medium">
                        Este boleto está vencido. Verifique com o beneficiário se ainda é possível pagar.
                      </p>
                    </div>
                  )}
                </div>

                {/* Campo de valor */}
                <div>
                  <label className="block text-xs font-medium text-slate-700 mb-1.5">
                    Valor da conta a pagar
                    {valorBloqueado && resultadoValidacao?.valor && resultadoValidacao.valor > 0 && (
                      <span className="ml-2 text-teal-600 font-normal">
                        <i className="ri-lock-line text-xs"></i> Identificado no código
                      </span>
                    )}
                    {resultadoValidacao?.valido && resultadoValidacao.valor === 0 && (
                      <span className="ml-2 text-amber-600 font-normal">
                        <i className="ri-edit-line text-xs"></i> Informe o valor
                      </span>
                    )}
                  </label>
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500 font-semibold text-sm">R$</span>
                    <input
                      type="text"
                      inputMode="decimal"
                      value={valorInput}
                      onChange={handleValorChange}
                      placeholder={valorBloqueado ? '' : '0,00'}
                      disabled={valorBloqueado}
                      className={`w-full pl-10 pr-10 py-3 border-2 rounded-xl text-lg font-bold focus:outline-none transition-all ${
                        valorExcedeSaldo
                          ? 'border-red-400 bg-red-50 text-red-700'
                          : erroValor
                          ? 'border-red-400 bg-red-50 text-red-700'
                          : valorNumerico > 0 && validacao.valido && !valorExcedeSaldo
                          ? 'border-teal-400 bg-teal-50 text-teal-700'
                          : valorBloqueado
                          ? 'border-teal-300 bg-teal-50/50 text-teal-700 cursor-not-allowed'
                          : 'border-slate-200 focus:border-teal-400'
                      }`}
                    />
                    {valorBloqueado && (
                      <div className="absolute right-3 top-1/2 -translate-y-1/2">
                        <i className="ri-lock-line text-teal-500"></i>
                      </div>
                    )}
                  </div>
                  <div className="flex justify-between mt-1">
                    <p className="text-xs text-slate-400">
                      Mín: {calcularPontosFinanceiro(limite.config.valorMinimo).toLocaleString('pt-BR')} pts •
                      Máx: {calcularPontosFinanceiro(Math.min(limite.config.valorMaximo, limite.valorDisponivel)).toLocaleString('pt-BR')} pts
                    </p>
                  </div>

                  {/* ⚠️ Aviso de valor excedendo saldo do crédito */}
                  {valorExcedeSaldo && creditoAtual && (
                    <div className="mt-2 flex items-start gap-2 bg-red-50 border border-red-300 rounded-xl p-3">
                      <i className="ri-error-warning-fill text-red-500 flex-shrink-0 mt-0.5 text-base"></i>
                      <div>
                        <p className="text-xs font-bold text-red-700">Valor excede o saldo disponível</p>
                        <p className="text-xs text-red-600 mt-0.5">
                          O valor informado (R$ {valorNumerico.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}) é maior que o saldo do crédito selecionado
                          (R$ {creditoAtual.valor.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}).
                          Selecione outro crédito ou informe um valor menor.
                        </p>
                      </div>
                    </div>
                  )}

                  {erroValor && !valorExcedeSaldo && (
                    <p className="text-xs text-red-500 mt-1 flex items-center gap-1">
                      <i className="ri-error-warning-line"></i>
                      {erroValor}
                    </p>
                  )}
                </div>

                {/* Cálculo dinâmico de pontos */}
                {valorNumerico > 0 && validacao.valido && !valorExcedeSaldo && (
                  <div className="bg-teal-50 border border-teal-200 rounded-xl p-4">
                    <div className="flex justify-between items-center mb-1">
                      <span className="text-sm text-slate-600">Pagar conta de</span>
                      <span className="text-lg font-bold text-teal-700">
                        R$ {valorNumerico.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                      </span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-slate-600">Custo</span>
                      <span className="text-lg font-bold text-orange-600">
                        {pontosNecessarios.toLocaleString('pt-BR')} pontos
                      </span>
                    </div>
                    {!saldoSuficiente && (
                      <p className="text-xs text-red-500 mt-2 flex items-center gap-1">
                        <i className="ri-error-warning-line"></i>
                        Pontos insuficientes para este valor.
                      </p>
                    )}
                  </div>
                )}

                <p className="text-xs text-slate-400 italic text-center">
                  Os valores em pontos podem variar conforme a recompensa escolhida.
                </p>

                <div className="flex gap-3">
                  <button
                    type="button"
                    onClick={animatedClose}
                    className="flex-1 py-3 bg-slate-100 text-slate-700 rounded-xl font-semibold hover:bg-slate-200 transition-colors whitespace-nowrap cursor-pointer"
                  >
                    Cancelar
                  </button>
                  <button
                    type="button"
                    onClick={handleContinuar}
                    disabled={!podeContinuar}
                    className={`flex-1 py-3 rounded-xl font-semibold transition-all whitespace-nowrap cursor-pointer ${
                      podeContinuar
                        ? 'bg-gradient-to-r from-teal-500 to-cyan-600 text-white hover:shadow-lg'
                        : 'bg-slate-200 text-slate-400 cursor-not-allowed'
                    }`}
                  >
                    Continuar
                    <i className="ri-arrow-right-line ml-2"></i>
                  </button>
                </div>
              </div>
            )}

            {/* ── ETAPA 2: CONFIRMAÇÃO ── */}
            {etapa === 'confirmacao' && resultadoValidacao && (
              <div className="space-y-4">
                <h3 className="font-semibold text-slate-800 flex items-center gap-2">
                  <i className="ri-file-list-3-line text-teal-500"></i>
                  Resumo do Pagamento
                </h3>

                <div className="bg-slate-50 rounded-xl p-4 space-y-3">
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-500">Beneficiário</span>
                    <span className="font-semibold text-slate-800">{resultadoValidacao.beneficiario || '—'}</span>
                  </div>
                  {resultadoValidacao.banco && (
                    <div className="flex justify-between text-sm">
                      <span className="text-slate-500">Banco</span>
                      <span className="font-semibold text-slate-800">{resultadoValidacao.banco}</span>
                    </div>
                  )}
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-500">Tipo</span>
                    <span className="font-semibold text-slate-800">
                      {resultadoValidacao.tipo === 'boleto' ? 'Boleto Bancário' : 'Conta de Consumo'}
                    </span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-500">Vencimento</span>
                    <span className="font-semibold text-slate-800">{vencimentoFormatado}</span>
                  </div>
                  <div className="border-t border-slate-200 pt-3 flex justify-between items-center">
                    <span className="text-sm text-slate-500">Pagar conta de</span>
                    <span className="text-xl font-bold text-teal-600">
                      R$ {valorNumerico.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                    </span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm font-bold text-slate-700">Custo em pontos</span>
                    <span className="text-xl font-bold text-orange-600">
                      {pontosNecessarios.toLocaleString('pt-BR')} pts
                    </span>
                  </div>
                </div>

                <div className="bg-slate-50 rounded-xl p-4 space-y-2">
                  <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Limite Mensal Após Pagamento</p>
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-600">Disponível após</span>
                    <span className="font-bold text-slate-800">
                      {calcularPontosFinanceiro(Math.max(0, limite.valorDisponivel - valorNumerico)).toLocaleString('pt-BR')} pts
                    </span>
                  </div>
                </div>

                <p className="text-xs text-slate-400 italic text-center">
                  Os valores em pontos podem variar conforme a recompensa escolhida.
                </p>

                <div className="flex gap-3">
                  <button
                    type="button"
                    onClick={() => setEtapa('formulario')}
                    className="flex-1 py-3 bg-slate-100 text-slate-700 rounded-xl font-semibold hover:bg-slate-200 transition-colors whitespace-nowrap cursor-pointer"
                  >
                    <i className="ri-arrow-left-line mr-2"></i>
                    Voltar
                  </button>
                  <button
                    type="button"
                    onClick={handleConfirmar}
                    className="flex-1 py-3 bg-gradient-to-r from-teal-500 to-cyan-600 text-white rounded-xl font-semibold hover:shadow-lg transition-all whitespace-nowrap cursor-pointer"
                  >
                    <i className="ri-shield-check-line mr-2"></i>
                    Confirmar e Verificar
                  </button>
                </div>
              </div>
            )}

            {/* ── ETAPA 3: TOKEN ── */}
            {etapa === 'token' && (
              <div className="space-y-5">
                <div className="text-center">
                  <div className="w-16 h-16 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-3">
                    <i className="ri-shield-keyhole-line text-3xl text-amber-500"></i>
                  </div>
                  <h3 className="font-bold text-slate-900 text-lg">Verificação de Segurança</h3>
                  <p className="text-sm text-slate-500 mt-1">
                    Enviamos um código de 6 dígitos para confirmar sua identidade.
                  </p>
                </div>

                <div className="bg-teal-50 border border-teal-200 rounded-xl p-3 text-sm text-center">
                  <span className="text-teal-700 font-semibold">
                    Pagar conta de R$ {valorNumerico.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                  </span>
                  <span className="text-slate-500 mx-2">•</span>
                  <span className="text-orange-600 font-semibold">
                    {pontosNecessarios.toLocaleString('pt-BR')} pontos
                  </span>
                </div>

                <div>
                  <label className="block text-xs font-medium text-slate-600 mb-2">Código de verificação</label>
                  <div className="flex justify-center gap-2" onPaste={handleTokenPaste}>
                    {tokenDigits.map((digit, index) => (
                      <input
                        key={index}
                        ref={(el) => { inputRefs.current[index] = el; }}
                        type="text"
                        inputMode="numeric"
                        maxLength={1}
                        value={digit}
                        onChange={(e) => handleTokenChange(index, e.target.value)}
                        onKeyDown={(e) => handleTokenKeyDown(index, e)}
                        className={`w-11 h-12 text-center text-lg font-bold border-2 rounded-lg focus:outline-none transition-all ${
                          tokenErro
                            ? 'border-red-400 bg-red-50 text-red-600'
                            : digit
                            ? 'border-teal-400 bg-teal-50 text-teal-700'
                            : 'border-slate-200 focus:border-teal-400'
                        }`}
                      />
                    ))}
                  </div>
                  {tokenErro && (
                    <p className="text-xs text-red-500 mt-2 text-center flex items-center justify-center gap-1">
                      <i className="ri-error-warning-line"></i>
                      {tokenErro}
                    </p>
                  )}
                </div>

                <div className="text-center text-sm text-slate-500">
                  {reenvioTimer > 0 ? (
                    <span>Reenviar em <span className="font-semibold text-teal-600">{reenvioTimer}s</span></span>
                  ) : (
                    <button
                      type="button"
                      onClick={() => { setReenvioTimer(60); setTokenDigits(['', '', '', '', '', '']); setTokenErro(''); }}
                      className="text-teal-600 font-semibold hover:underline cursor-pointer"
                    >
                      <i className="ri-refresh-line mr-1"></i>
                      Reenviar código
                    </button>
                  )}
                </div>

                <div className="flex gap-3">
                  <button
                    type="button"
                    onClick={() => setEtapa('confirmacao')}
                    disabled={processando}
                    className="flex-1 py-3 bg-slate-100 text-slate-700 rounded-xl font-semibold hover:bg-slate-200 transition-colors whitespace-nowrap cursor-pointer disabled:opacity-50"
                  >
                    <i className="ri-arrow-left-line mr-2"></i>
                    Voltar
                  </button>
                  <button
                    type="button"
                    onClick={handleValidarToken}
                    disabled={!tokenCompleto || processando}
                    className="flex-1 py-3 bg-gradient-to-r from-teal-500 to-cyan-600 text-white rounded-xl font-semibold hover:shadow-lg transition-all whitespace-nowrap cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                  >
                    {processando ? (
                      <><i className="ri-loader-4-line animate-spin"></i> Validando...</>
                    ) : (
                      <><i className="ri-check-line"></i> Validar e Concluir</>
                    )}
                  </button>
                </div>
              </div>
            )}

            {/* ── ETAPA 4: SUCESSO ── */}
            {etapa === 'sucesso' && resultadoValidacao && (
              <div className="text-center space-y-5">
                <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto">
                  <i className="ri-checkbox-circle-fill text-5xl text-green-500"></i>
                </div>
                <div>
                  <h3 className="text-xl font-bold text-slate-900 mb-1">Pagamento Realizado! 🎉</h3>
                  <p className="text-slate-500 text-sm">Sua conta foi paga com sucesso.</p>
                </div>

                <div className="bg-slate-50 rounded-xl p-4 text-left space-y-3">
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-500">ID da Transação</span>
                    <span className="font-mono font-bold text-slate-800 text-xs">{transacaoId.current}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-500">Beneficiário</span>
                    <span className="font-semibold text-slate-800">{resultadoValidacao.beneficiario || '—'}</span>
                  </div>
                  <div className="border-t border-slate-200 pt-3 flex justify-between items-center">
                    <span className="text-sm text-slate-500">Conta paga</span>
                    <span className="text-xl font-bold text-teal-600">
                      R$ {valorNumerico.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                    </span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-slate-500">Pontos utilizados</span>
                    <span className="text-lg font-bold text-orange-600">
                      {pontosNecessarios.toLocaleString('pt-BR')} pts
                    </span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-500">Data e Hora</span>
                    <span className="font-semibold text-slate-800">{dataHora.current}</span>
                  </div>
                </div>

                <div className="bg-slate-50 rounded-xl p-4 space-y-2">
                  <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Limite Mensal Atualizado</p>
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-600">Disponível restante</span>
                    <span className="font-bold text-teal-600">
                      {calcularPontosFinanceiro(limite.valorDisponivel).toLocaleString('pt-BR')} pts
                    </span>
                  </div>
                  <div className="h-2 bg-slate-200 rounded-full overflow-hidden">
                    <div
                      className="h-full bg-gradient-to-r from-orange-400 to-orange-500 rounded-full"
                      style={{ width: `${limite.percentualUtilizado}%` }}
                    />
                  </div>
                </div>

                <button
                  type="button"
                  onClick={animatedClose}
                  className="w-full py-3 bg-gradient-to-r from-teal-500 to-cyan-600 text-white rounded-xl font-semibold hover:shadow-lg transition-all whitespace-nowrap cursor-pointer"
                >
                  Fechar
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
}
